import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, ShieldCheck, ShieldAlert, Trash2, Save, Loader2, CreditCard, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import AdminInvoices from "@/components/AdminInvoices";
import TestSubscription from "@/components/TestSubscription";
import AdminEmailLog from "@/components/AdminEmailLog";
import AdminApifyUsage from "@/components/AdminApifyUsage";
import AdminCountryCounts from "@/components/AdminCountryCounts";

const STATUSES = [
  { v: "trialing", label: "Essai" },
  { v: "active", label: "Actif" },
  { v: "past_due", label: "Paiement en attente" },
  { v: "canceled", label: "Résilié" },
  { v: "expired", label: "Expiré" },
];
const PLANS = [
  { v: "starter", label: "Starter" },
  { v: "pro", label: "Pro" },
  { v: "business", label: "Business" },
];
const toDateInput = (iso) => { try { return iso ? new Date(iso).toISOString().slice(0, 10) : ""; } catch { return ""; } };

export default function AdminClients() {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [stripeBusy, setStripeBusy] = useState(false);

  const loadStripe = () => api.get("/admin/stripe/status").then((r) => setStripe(r.data)).catch(() => {});
  const setupStripe = async () => {
    setStripeBusy(true);
    try {
      const r = await api.post("/admin/stripe/setup-products");
      toast.success(`Produits Stripe créés/vérifiés : ${(r.data.created || []).join(", ") || "déjà en place"}`);
      loadStripe();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Création des produits impossible.");
    } finally { setStripeBusy(false); }
  };
  const copyWebhook = () => { if (stripe?.webhook_url) { navigator.clipboard?.writeText(stripe.webhook_url); toast.success("URL du webhook copiée."); } };

  const load = () => api.get("/admin/clients").then((r) => {
    setData(r.data);
    const e = {};
    r.data.clients.forEach((c) => {
      e[c.id] = { status: c.subscription.status, plan: c.subscription.plan || "", end: toDateInput(c.subscription.current_period_end) };
    });
    setEdits(e);
  }).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));

  useEffect(() => { load(); loadStripe(); /* eslint-disable-next-line */ }, []);

  const setField = (id, k, v) => setEdits((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));

  const save = async (c) => {
    const e = edits[c.id];
    setSaving(c.id);
    try {
      const payload = { status: e.status };
      if (e.plan) payload.plan = e.plan;
      if (e.end) payload.current_period_end = new Date(e.end + "T23:59:59").toISOString();
      await api.patch(`/admin/clients/${c.id}/subscription`, payload);
      toast.success(`Abonnement de ${c.email} mis à jour`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de la mise à jour");
    } finally { setSaving(""); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/clients/${deleting.id}`);
      toast.success("Client supprimé");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Suppression impossible");
    }
  };

  if (!data) return <div className="p-8 text-slate-400" data-testid="admin-loading">Chargement…</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 lg:px-10" data-testid="admin-clients-page">
      <header className="mb-8">
        <p className="overline text-[#C6A87C]">Super Administrateur</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">Clients & abonnements</h1>
        <p className="mt-2 text-slate-500">Vue globale des comptes clients et pilotage de leurs statuts d'abonnement.</p>
      </header>

      <AdminApifyUsage />

      <AdminCountryCounts />

      <div className="mb-8 grid grid-cols-3 gap-4">
        {[
          { k: "total", label: "Clients", icon: Users, cls: "text-[#0F172A]" },
          { k: "active", label: "Accès actif", icon: ShieldCheck, cls: "text-emerald-600" },
          { k: "inactive", label: "Accès bloqué", icon: ShieldAlert, cls: "text-red-600" },
        ].map((s) => (
          <div key={s.k} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`admin-stat-${s.k}`}>
            <div className="flex items-center gap-2 text-slate-400"><s.icon className="h-4 w-4" /><span className="text-xs">{s.label}</span></div>
            <p className={`mt-2 font-display text-4xl ${s.cls}`}>{data.stats[s.k]}</p>
          </div>
        ))}
      </div>

      {/* Configuration facturation Stripe (abonnements récurrents) */}
      {stripe && (
        <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-stripe-card">
          <h2 className="mb-1 flex items-center gap-2 font-display text-xl text-[#0F172A]"><CreditCard className="h-5 w-5 text-[#C6A87C]" /> Facturation Stripe (abonnements récurrents)
            {stripe.mode === "live" ? (
              <span data-testid="admin-stripe-mode-badge" className="ml-2 inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Mode LIVE
              </span>
            ) : stripe.mode === "test" ? (
              <span data-testid="admin-stripe-mode-badge" className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" /> Mode TEST
              </span>
            ) : null}
          </h2>
          <p className="mb-4 text-sm text-slate-500">Essai 24 h avec CB obligatoire, facturation mensuelle automatique, suspension/réactivation auto selon le paiement.</p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className={`rounded-xl border p-3 ${stripe.key_configured ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="admin-stripe-key">
              <div className="flex items-center gap-2 text-sm font-semibold">
                {stripe.key_configured ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                Clé Stripe
              </div>
              <p className="mt-1 text-xs text-slate-500">{stripe.key_configured ? "Clé native détectée." : "Clé sk_test_… requise dans STRIPE_API_KEY."}</p>
            </div>
            <div className={`rounded-xl border p-3 ${stripe.all_prices_set ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid="admin-stripe-prices">
              <div className="flex items-center gap-2 text-sm font-semibold">
                {stripe.all_prices_set ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-slate-400" />}
                Produits & prix
              </div>
              <p className="mt-1 text-xs text-slate-500">{stripe.all_prices_set ? "3 formules mappées." : "À créer (69/149/189 €)."}</p>
            </div>
            <div className={`rounded-xl border p-3 ${stripe.webhook_secret_set ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid="admin-stripe-webhook">
              <div className="flex items-center gap-2 text-sm font-semibold">
                {stripe.webhook_secret_set ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-slate-400" />}
                Webhook
              </div>
              <p className="mt-1 text-xs text-slate-500">{stripe.webhook_secret_set ? "Secret configuré." : "Secret whsec_… à ajouter."}</p>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-3">
            <p className="text-xs font-medium text-slate-500">URL du webhook à enregistrer dans Stripe</p>
            <div className="mt-1 flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-white px-2 py-1.5 text-xs text-slate-700" data-testid="admin-stripe-webhook-url">{stripe.webhook_url}</code>
              <Button variant="outline" size="sm" onClick={copyWebhook} data-testid="admin-stripe-copy-webhook" className="h-8 shrink-0 rounded-full"><Copy className="mr-1 h-3.5 w-3.5" /> Copier</Button>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Événements : checkout.session.completed, customer.subscription.*, invoice.paid, invoice.payment_failed. Puis renseignez STRIPE_WEBHOOK_SECRET.</p>
          </div>

          <Button onClick={setupStripe} disabled={stripeBusy || !stripe.key_configured} data-testid="admin-stripe-setup-btn"
                  className="mt-4 h-10 rounded-full bg-[#0F172A] px-6 text-white hover:bg-[#1e293b]">
            {stripeBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />} Créer / vérifier les produits Stripe
          </Button>
          {!stripe.key_configured && <p className="mt-2 text-xs text-amber-700" data-testid="admin-stripe-key-hint">{stripe.key_hint}</p>}
        </div>
      )}

      <TestSubscription clients={data.clients} onChange={load} />

      <AdminInvoices clients={data.clients} />

      <AdminEmailLog />

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Client</th>
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium">Formule</th>
              <th className="px-4 py-3 font-medium">Échéance</th>
              <th className="px-4 py-3 font-medium">Accès</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.clients.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Aucun client inscrit pour le moment.</td></tr>
            )}
            {data.clients.map((c) => {
              const e = edits[c.id] || {};
              return (
                <tr key={c.id} className="border-b border-slate-100 last:border-0" data-testid={`admin-client-${c.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#0F172A]">{c.name || "—"}</p>
                    <p className="text-xs text-slate-400">{c.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={e.status} onValueChange={(v) => setField(c.id, "status", v)}>
                      <SelectTrigger className="h-9 w-40" data-testid={`admin-status-${c.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={e.plan || "none"} onValueChange={(v) => setField(c.id, "plan", v === "none" ? "" : v)}>
                      <SelectTrigger className="h-9 w-32" data-testid={`admin-plan-${c.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {PLANS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <Input type="date" value={e.end || ""} onChange={(ev) => setField(c.id, "end", ev.target.value)} className="h-9 w-40" data-testid={`admin-end-${c.id}`} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${c.radar_access ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {c.radar_access ? "Actif" : "Bloqué"}
                    </span>
                    {c.subscription?.test && (
                      <span className="ml-1.5 inline-flex rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase text-amber-700" data-testid={`admin-test-badge-${c.id}`}>Test</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" onClick={() => save(c)} disabled={saving === c.id} className="h-9 rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid={`admin-save-${c.id}`}>
                        {saving === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-3.5 w-3.5" /> Enregistrer</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeleting(c)} className="h-9 rounded-full text-red-600 hover:bg-red-50" data-testid={`admin-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {deleting?.email} » et toutes ses données RADAR seront définitivement supprimés. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="admin-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-full bg-red-600 hover:bg-red-700" data-testid="admin-delete-confirm">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
